import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Circle, X, Sparkles } from 'lucide-react';
import { photographerAPI } from '@services/photographerApi';
import type { OnboardingStatus } from '@/types/photographer';

const SetupWidget: React.FC = () => {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    photographerAPI
      .getOnboardingStatus()
      .then(res => { if (active) setStatus(res.data); })
      .catch(() => { /* widget is non-critical; stay hidden on error */ });
    return () => { active = false; };
  }, []);

  if (!status || status.completed >= status.total) return null;

  return (
    <div data-testid="setup-widget" className="fixed bottom-4 right-4 z-50">
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-lg hover:bg-blue-700"
        >
          <Sparkles className="h-4 w-4" /> Setup {status.completed}/{status.total}
        </button>
      )}
      {open && (
        <div className="w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between bg-blue-600 px-4 py-3 text-white">
            <span className="font-semibold">Get your gallery ready</span>
            <span className="flex items-center gap-2 text-sm">
              {status.completed}/{status.total}
              <button aria-label="Collapse setup" onClick={() => setOpen(false)}><X className="h-4 w-4" /></button>
            </span>
          </div>
          <div className="px-4 py-3">
            <div className="mb-3 h-1.5 rounded-full bg-gray-200">
              <div className="h-1.5 rounded-full bg-green-500"
                   style={{ width: `${(status.completed / status.total) * 100}%` }} />
            </div>
            <ul className="space-y-2 text-sm">
              {status.steps.map(step => (
                <li key={step.key} className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    {step.done
                      ? <CheckCircle className="h-4 w-4 text-green-500" />
                      : <Circle className="h-4 w-4 text-gray-300" />}
                    <span className={step.done ? 'text-gray-400 line-through' : 'text-gray-800'}>{step.label}</span>
                  </span>
                  {!step.done && (
                    <Link to={step.cta} className="text-xs font-medium text-blue-600 hover:text-blue-500">Go</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetupWidget;
